import { SceneConfig } from "appworks/config/asset-config-schema";
import { DOMLayer } from "appworks/graphics/layers/dom-layer";
import { SceneTransition } from "appworks/graphics/layers/scene-transitions/scene-transition";
import { Contract } from "appworks/utils/contracts/contract";
import { DualPosition } from "../pixi/dual-position";
import { Position } from "../pixi/position";

export class Scene {

    public positions: Map<string, DualPosition> = new Map<string, DualPosition>();

    private transitions: Map<string, SceneTransition> = new Map<string, SceneTransition>();

    private activeTransition: SceneTransition;
    private transitionState: TransitionState;

    constructor(public name: string, public layer: string, public config?: SceneConfig) {
        if (this.config) {
            if (this.config.positions) {
                for (const positionName in this.config.positions) {
                    if (this.config.positions.hasOwnProperty(positionName)) {
                        const positionConfig = this.config.positions[positionName];

                        const position: DualPosition = new DualPosition(null, null);
                        if (positionConfig.landscape) {
                            position.landscape = new Position
                                (positionConfig.landscape.x,
                                    positionConfig.landscape.y,
                                    positionConfig.landscape.width,
                                    positionConfig.landscape.height);
                        } else {
                            position.landscape = Position.unavailable();
                        }
                        if (positionConfig.portrait) {
                            position.portrait = new Position(
                                positionConfig.portrait.x,
                                positionConfig.portrait.y,
                                positionConfig.portrait.width,
                                positionConfig.portrait.height);
                        } else {
                            position.portrait = Position.unavailable();
                        }

                        this.positions.set(positionName, position);
                    }
                }
            }
        }
    }

    public setTransitions(transitions: SceneTransition, scene: string) {
        this.transitions.set(scene, transitions);
    }

    public in(toScene: string = "*"): Contract<void> {
        let transition: SceneTransition;
        if (this.transitions.has(toScene)) {
            transition = this.transitions.get(toScene);
        } else if (this.transitions.has("*")) {
            transition = this.transitions.get("*");
        }

        if (transition) {
            this.transitionState = TransitionState.IN;
            this.activeTransition = transition;

            return new Contract<void>((resolve) => {
                transition.in().then(() => {
                    this.clearTransitionState();
                    resolve(null);
                });
            });
        }

        return Contract.empty();
    }

    public out(fromScene: string = "*"): Contract<void> {
        let transition: SceneTransition;
        if (this.transitions.has(fromScene)) {
            transition = this.transitions.get(fromScene);
        } else if (this.transitions.has("*")) {
            transition = this.transitions.get("*");
        }

        if (transition) {
            this.transitionState = TransitionState.OUT;
            this.activeTransition = transition;

            return new Contract<void>((resolve) => {
                transition.out().then(() => {
                    this.clearTransitionState();
                    resolve(null);
                });
            });
        }

        return Contract.empty();
    }

    public skipTransition() {
        if (this.transitionState) {
            if (this.transitionState === TransitionState.IN) {
                this.activeTransition.skipIn();
            } else {
                this.activeTransition.skipOut();
            }

            this.clearTransitionState();
        }
    }

    public hasCanvas(): boolean {
        return this.config !== null;
    }

    public hasDOMScene(): boolean {
        return DOMLayer.hasDOMScene(this.name, this.layer);
    }

    public clearDOM() {
        DOMLayer.setDOMScene(this.name, this.layer, false);
    }

    public showDOM() {
        if (this.hasDOMScene()) {
            DOMLayer.setDOMScene(this.name, this.layer, true);
        }
    }

    public destroyTransitions() {
        if (this.activeTransition) {
            this.activeTransition.destroy();
            this.activeTransition = null;
        }
    }

    private clearTransitionState() {
        this.transitionState = null;
    }
}

export enum TransitionState {
    IN = "in",
    OUT = "out"
}
